import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

export default function ClientDirectory() {
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');
  const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [healthFilter, setHealthFilter] = useState('');

  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', organization_name: '', email: '', phone: '', status: 'prospect' });
  const [saving, setSaving] = useState(false);

  const fetchClients = () => {
    if (!companyId) return;
    setLoading(true);
    const params = new URLSearchParams({ page, per_page: 15 });
    if (search) params.append('search', search);
    if (statusFilter) params.append('status', statusFilter);
    if (healthFilter) params.append('health', healthFilter);

    fetch(`${API}/companies/${companyId}/clients?${params}`, { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => {
        setClients(Array.isArray(d.data) ? d.data : []);
        setTotalPages(d.last_page || 1);
      })
      .catch(() => setClients([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchClients(); }, [companyId, page, search, statusFilter, healthFilter, API]);

  const handleCreate = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/clients`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setShowCreate(false);
      setForm({ name: '', organization_name: '', email: '', phone: '', status: 'prospect' });
      setPage(1);
      fetchClients();
    } catch { alert('Failed to create client'); }
    finally { setSaving(false); }
  };

  const healthColor = { good: '#22c55e', attention_needed: '#f59e0b', at_risk: '#ef4444' };
  const statusColor = { prospect: '#8b5cf6', active: '#10b981', on_hold: '#f59e0b', inactive: '#6b7280', archived: '#94a3b8' };

  if (!companyId) return <div style={pageStyle}><p>Please select a company first.</p></div>;

  return (
    <div style={pageStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>📂 Client Directory</h2>
        <button onClick={() => setShowCreate(true)} style={btnPrimary}>+ New Client</button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <input
          placeholder="Search clients..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          style={inputStyle}
        />
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} style={selectStyle}>
          <option value="">All Statuses</option>
          <option value="prospect">Prospect</option>
          <option value="active">Active</option>
          <option value="on_hold">On Hold</option>
          <option value="inactive">Inactive</option>
        </select>
        <select value={healthFilter} onChange={e => { setHealthFilter(e.target.value); setPage(1); }} style={selectStyle}>
          <option value="">All Health</option>
          <option value="good">Good</option>
          <option value="attention_needed">Attention Needed</option>
          <option value="at_risk">At Risk</option>
        </select>
      </div>

      {initialLoading ? <p>Loading...</p> : (
        <>
          <div style={{ ...cardStyle, overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Client</th>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Health</th>
                  <th style={thStyle}>Owner</th>
                  <th style={thStyle}>Primary Contact</th>
                  <th style={thStyle}>Services</th>
                  <th style={thStyle}>Last Activity</th>
                </tr>
              </thead>
              <tbody>
                {clients.length === 0 ? (
                  <tr><td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No clients found</td></tr>
                ) : clients.map(c => (
                  <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/company/clients/${c.id}`)}>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 600 }}>{c.display_name || c.name}</div>
                      <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{c.client_code}</div>
                    </td>
                    <td style={tdStyle}>{c.client_type?.name || '—'}</td>
                    <td style={tdStyle}>
                      <span style={{ ...badgeStyle, background: (statusColor[c.status] || '#6b7280') + '20', color: statusColor[c.status] || '#6b7280' }}>
                        {(c.status || '').replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ ...badgeStyle, background: (healthColor[c.health] || '#6b7280') + '20', color: healthColor[c.health] || '#6b7280' }}>
                        {(c.health || 'good').replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td style={tdStyle}>{c.account_owner?.name || '—'}</td>
                    <td style={tdStyle}>
                      {c.primary_contact ? `${c.primary_contact.first_name} ${c.primary_contact.last_name || ''}` : '—'}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>{c.active_services_count || 0}</td>
                    <td style={tdStyle}>
                      {c.last_activity_at ? new Date(c.last_activity_at).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px' }}>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} style={paginationBtn}>← Prev</button>
              <span style={{ padding: '6px 12px', fontSize: '0.85rem', color: '#64748b' }}>Page {page} of {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={paginationBtn}>Next →</button>
            </div>
          )}
        </>
      )}

      {/* Create Modal */}
      {showCreate && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <h3 style={{ margin: '0 0 16px', fontSize: '1.1rem', fontWeight: 700 }}>Create New Client</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={labelStyle}>Client Name *</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={modalInputStyle} placeholder="Client name" />
              </div>
              <div>
                <label style={labelStyle}>Organization Name</label>
                <input value={form.organization_name} onChange={e => setForm({ ...form, organization_name: e.target.value })} style={modalInputStyle} placeholder="Organization" />
              </div>
              <div>
                <label style={labelStyle}>Email</label>
                <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} style={modalInputStyle} placeholder="email@example.com" />
              </div>
              <div>
                <label style={labelStyle}>Phone</label>
                <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} style={modalInputStyle} placeholder="+1 234 567 890" />
              </div>
              <div>
                <label style={labelStyle}>Status</label>
                <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} style={modalInputStyle}>
                  <option value="prospect">Prospect</option>
                  <option value="active">Active</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowCreate(false)} style={btnCancel}>Cancel</button>
              <button onClick={handleCreate} disabled={saving || !form.name} style={btnPrimary}>{saving ? 'Creating...' : 'Create Client'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const pageStyle = { padding: '24px', maxWidth: '1200px' };
const cardStyle = { background: '#fff', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' };
const thStyle = { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e2e8f0', color: '#64748b', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' };
const tdStyle = { padding: '10px 12px', borderBottom: '1px solid #f1f5f9', color: '#334155' };
const badgeStyle = { padding: '2px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600, textTransform: 'capitalize' };
const inputStyle = { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.85rem', minWidth: '200px' };
const selectStyle = { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.85rem', background: '#fff' };
const btnPrimary = { padding: '8px 16px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' };
const btnCancel = { padding: '8px 16px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', fontSize: '0.85rem', cursor: 'pointer' };
const paginationBtn = { padding: '6px 14px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.85rem', cursor: 'pointer' };
const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalStyle = { background: '#fff', borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '500px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' };
const labelStyle = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '4px' };
const modalInputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.85rem', boxSizing: 'border-box' };
